/**
 * GraphQL Hooks for Collections
 *
 * Apollo Client hooks for collection operations with automatic
 * transformation to REST-compatible types for component compatibility.
 *
 * Feature: 063-graphql-frontend
 * Task: T015
 */

import { useQuery, useApolloClient } from '@apollo/client';
import {
  GET_COLLECTIONS,
  GET_COLLECTION,
} from '../queries';
import {
  mapGraphQLCollectionToREST,
  mapCollectionItemsToViewModels,
} from '../mappers';
import type {
  GraphQLCollection,
  PaginatedResult,
  GraphQLCollectionItem,
} from '../types';
import type { Collection } from '../../types/collection';
import type { PhotoItem2ViewModel } from '../../components/PhotoItem2/PhotoItem2.types';
import logger from '../../utils/logger';

interface GetCollectionsData {
  collections: PaginatedResult<GraphQLCollection> & {
    hasMore: boolean;
    limit: number;
    offset: number;
  };
}

interface GetCollectionData {
  collection: GraphQLCollection & {
    items?: PaginatedResult<GraphQLCollectionItem>;
  };
}

/**
 * Query: List all collections for authenticated user with infinite scroll support
 *
 * Returns collections with loadMore function for infinite scroll.
 * @param limit - Number of collections per page (default: 20)
 *
 * Note: Kept at 20 to avoid CDN rate limiting when loading hero images.
 * Infinite scroll will load more as user scrolls.
 */
export function useCollectionsGraphQL(limit: number = 20) {
  const { data, loading, error, refetch, fetchMore } = useQuery<GetCollectionsData>(GET_COLLECTIONS, {
    variables: { limit, offset: 0 },
    fetchPolicy: 'cache-and-network',
  });

  /**
   * Load more collections using cursor-based pagination.
   * Automatically merges new results with existing data.
   * Handles errors gracefully with logging.
   */
  const loadMore = async () => {
    if (!data?.collections.hasMore) {
      return;
    }

    const currentOffset = data.collections.items.length;

    try {
      await fetchMore({
        variables: {
          limit,
          offset: currentOffset,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          return {
            collections: {
              ...fetchMoreResult.collections,
              items: [...prev.collections.items, ...fetchMoreResult.collections.items],
            },
          };
        },
      });
    } catch (err) {
      logger.error('useCollectionsGraphQL', 'Failed to load more collections', err);
    }
  };

  // Transform to component-compatible format
  const collections = data?.collections.items.map(mapGraphQLCollectionToREST) ?? [];

  return {
    collections,
    totalCount: data?.collections.totalCount ?? 0,
    hasMore: data?.collections.hasMore ?? false,
    loading,
    error,
    refetch,
    loadMore,
  };
}

/**
 * Query: Get single collection by ID with photos
 *
 * Returns collection data and photo items in REST-compatible format.
 */
export function useCollectionGraphQL(collectionId: string | undefined) {
  const { data, loading, error, refetch } = useQuery<GetCollectionData>(GET_COLLECTION, {
    // CRITICAL: Always provide a placeholder ID when skipping to prevent Apollo Client
    // from validating required variables before checking skip.
    variables: { id: collectionId || 'skip-placeholder' },
    skip: !collectionId,
    fetchPolicy: 'cache-and-network',
  });

  // Transform to REST-compatible format
  const collection: Collection | undefined = data?.collection
    ? mapGraphQLCollectionToREST(data.collection)
    : undefined;

  // Transform collection items to PhotoItem2ViewModel
  const items: PhotoItem2ViewModel[] = data?.collection?.items
    ? mapCollectionItemsToViewModels(data.collection.items)
    : [];

  return {
    data: collection,
    items,
    isLoading: loading,
    isError: !!error,
    error,
    refetch,
  };
}

/**
 * Helper to invalidate collection queries after mutations
 */
export function useInvalidateCollections() {
  const client = useApolloClient();

  return {
    invalidateCollections: () => {
      client.refetchQueries({
        include: ['GetCollections'],
      });
    },
    invalidateCollection: (_collectionId: string) => {
      client.refetchQueries({
        include: ['GetCollection'],
      });
    },
  };
}
